import React from "react";
import {
  Box,
  Button,
  InputBase,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { verfiyOtpThunk } from "../redux/thunk/loginThunk";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { OtpSchema } from "../formikSchema";
import type { AppDispatch } from "@store/store";
import { toast } from "react-toastify";
import { setToken, setIsUserLoggedIn } from "../redux/loginSlice";
import ArrowBack from "@assets/images/logo/arrow_back.svg";

interface OtpFormProps {
  mobile: string;
  onResendOtp: () => Promise<boolean>;
  handleBack: () => void;
}

const OtpForm = ({ mobile, onResendOtp, handleBack }: OtpFormProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [otp, setOtp] = useState(new Array(5).fill(""));
  const [timer, setTimer] = useState(35);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const handleResend = async () => {
    if (canResend) {
      const success = await onResendOtp();
      if (success) {
        setTimer(35);
        setCanResend(false);
        setOtp(new Array(5).fill(""));
        formikOTP.setFieldValue("otp", "");
      }
    }
  };

  const formikOTP = useFormik({
    initialValues: {
      otp: ""
    },
    validationSchema: OtpSchema,
    onSubmit: async (values: { otp: string }) => {
      const verifyReponse = await dispatch(
        verfiyOtpThunk({
          mobile_number: "+91" + mobile,
          otp_code: values.otp
        })
      );
      if (verifyReponse.meta.requestStatus === "fulfilled") {
        if (verifyReponse.payload?.redirect) {
          window.location.href = verifyReponse.payload.redirectURL;
        } else {
          const token = verifyReponse.payload?.data?.token;
          if (token) {
            dispatch(setToken(token));
            dispatch(setIsUserLoggedIn(true));
            navigate("/application-dashboard");
          } else {
            console.error("Token is missing in the response payload.");
            toast.error("Failed to retrieve token. Please try again.");
          }
        }
      } else {
        console.error(
          "OTP verification failed:",
          verifyReponse?.payload?.message
        );
        toast.error(verifyReponse?.payload?.message || "Invalid OTP");
      }
    }
  });

  return (
    <form
      onSubmit={formikOTP.handleSubmit}
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column", 
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: { xs: "100%", md: 400 },
          mx: "auto",
          my: 1,
          borderRadius: 1,
          p: { xs: 3, md: 3 },
          bgcolor: "#fff",
          transform: { xs: "translateY(-8px)", md: "translateY(-30px)" }
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={0.5}
        >
          {/* <Box sx={{ mb: 2, textAlign: "left" }}>
            <img
              className="cursor-pointer"
              src={ArrowBack}
              alt="Arrow Back"
              style={{ width: "2rem" }}
              onClick={handleBack}
            />
          </Box> */}
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <img
              className="cursor-pointer"
              src={ArrowBack}
              alt="Arrow Back"
              style={{ width: "2rem" }}
              onClick={handleBack}
            />

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 24 },
                lineHeight: 1.1,
                color: "#212121"
              }}
            >
              Verify with OTP
            </Typography>
          </Box>
          {/* <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 24 },
              lineHeight: 1.1,
              color: "#212121"
            }}
          >
            Verify with OTP
          </Typography> */}
        </Stack>

        <Typography
          sx={{
            color: "#212121",
            display: "block",
            mb: 1.5,
            fontWeight: 400,
            fontSize: 14
          }}
          component="label"
          className="aadhaar-register-label"
        >
          Enter the OTP sent to your registered mobile number
        </Typography>

        {/* OTP Inputs */}
        <Box display="flex" justifyContent="center" mt={2} gap="1rem">
          {otp.map((item, index) => (
            <InputBase
              key={index}
              value={item}
              inputRef={el => (inputRefs.current[index] = el)}
              onChange={e => {
                const value = e.target.value;
                if (/^\d?$/.test(value)) {
                  const newOtp = [...otp];
                  newOtp[index] = value;
                  setOtp(newOtp);
                  formikOTP.setFieldValue("otp", newOtp.join(""));
                  if (value && index < otp.length - 1) {
                    inputRefs.current[index + 1]?.focus();
                  }
                }
              }}
              onKeyDown={e => {
                if (e.key === "Backspace") {
                  if (otp[index]) {
                    const newOtp = [...otp];
                    newOtp[index] = "";
                    setOtp(newOtp);
                    formikOTP.setFieldValue("otp", newOtp.join(""));
                  } else if (index > 0) {
                    const newOtp = [...otp];
                    newOtp[index - 1] = "";
                    setOtp(newOtp);
                    formikOTP.setFieldValue("otp", newOtp.join(""));
                    inputRefs.current[index - 1]?.focus();
                  }
                  e.preventDefault();
                }
              }}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  fontSize: "1rem"
                }
              }}
              sx={{
                width: "3.5rem",
                height: "3.5rem",
                border: "1px solid",
                borderColor:
                  formikOTP.touched.otp && formikOTP.errors.otp
                    ? "#d32f2f"
                    : "#CFCFCF",
                borderRadius: "6px",
                outline: "none",
                "&:focus-within": {
                  borderColor: "#3a1c73"
                }
              }}
            />
          ))}
        </Box>

        {/* Show OTP error message */}
        {formikOTP.touched.otp && formikOTP.errors.otp && (
          <Typography
            color="error"
            variant="caption"
            textAlign="center"
            display="block"
            fontSize="10px"
            fontWeight="500"
            mt={1}
          >
            {formikOTP.errors.otp}
          </Typography>
        )}

        <Box textAlign="center" mt={2}>
          <Typography mb={1}>
            {canResend ? (
              <strong
                className="resend-text"
                onClick={handleResend}
                style={{
                  cursor: "pointer",
                  color: "#3a1c73",
                  textDecoration: "underline"
                }}
              >
                Resend OTP
              </strong>
            ) : (
              <>
                <strong className="resend-text">Resend</strong> in{" "}
                {formatTime(timer)}
              </>
            )}
          </Typography>
          <Button className="btnContinue" type="submit" fullWidth sx={{marginBottom: "0 !important"}}>
            Continue
          </Button>
        </Box>
      </Paper>
    </form>
  );
};

export default OtpForm;
